import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  LockOutlined as LockOutlinedIcon,
} from "@mui/icons-material";
import { selectRoles } from "../../features/roleSlice";
import {
  DEFAULT_SPEC_SHEET_CONFIG,
  AREA_LABELS,
  FIELD_TYPES,
  makeFieldId,
} from "../../components/SBCF/SpecSheet/specSheetConfig";
import {
  fetchSpecSheetFormConfig,
  saveSpecSheetFormConfig,
  resetSpecSheetFormConfig,
} from "../../components/SBCF/SpecSheet/api";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import { SectionHeader } from "../../components/UI/Page";
import { EmptyState, LoadingState } from "../../components/UI/States";
import { Notice } from "../../components/SBCF/ui";

export const SPEC_SHEET_ADMIN_ROLES = ["Admin", "SBCF Admin"];

const clone = (obj) => JSON.parse(JSON.stringify(obj));

const SpecSheetFormAdmin = () => {
  const role = useSelector(selectRoles);
  const allowed = SPEC_SHEET_ADMIN_ROLES.some((r) =>
    role?.roles?.includes(r)
  );

  const [config, setConfig] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState(null); // { type: "success" | "error", message }
  const [saving, setSaving] = useState(false);
  const [expandedOptions, setExpandedOptions] = useState({});

  // The field editor is built entirely from MUI controls, whose palette is
  // independent of Tailwind's `dark:` classes. Drive it from the in-app toggle
  // so labels/values aren't near-black on the dark cards.
  const muiTheme = useAppMuiTheme();

  useEffect(() => {
    fetchSpecSheetFormConfig()
      .then((saved) =>
        setConfig(
          saved && Array.isArray(saved.sections)
            ? saved
            : clone(DEFAULT_SPEC_SHEET_CONFIG)
        )
      )
      .catch(() => setConfig(clone(DEFAULT_SPEC_SHEET_CONFIG)));
  }, []);

  if (!allowed) {
    return (
      <div className="app-card rounded-2xl border">
        <EmptyState
          icon={LockOutlinedIcon}
          title="Form Admin"
          description="You need the Admin or SBCF Admin role to edit this form."
        />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="app-card rounded-2xl border">
        <LoadingState label="Loading form configuration..." />
      </div>
    );
  }

  const touch = (updater) => {
    setConfig((prev) => {
      const next = clone(prev);
      updater(next);
      return next;
    });
    setDirty(true);
    setStatus(null);
  };

  const updateSection = (sectionId, patch) =>
    touch((cfg) => {
      const section = cfg.sections.find((s) => s.id === sectionId);
      if (section) Object.assign(section, patch);
    });

  const updateField = (sectionId, index, patch) =>
    touch((cfg) => {
      const section = cfg.sections.find((s) => s.id === sectionId);
      if (section?.fields?.[index])
        Object.assign(section.fields[index], patch);
    });

  const moveField = (sectionId, index, direction) =>
    touch((cfg) => {
      const section = cfg.sections.find((s) => s.id === sectionId);
      if (!section) return;
      const target = index + direction;
      if (target < 0 || target >= section.fields.length) return;
      const [field] = section.fields.splice(index, 1);
      section.fields.splice(target, 0, field);
    });

  const removeField = (sectionId, index) =>
    touch((cfg) => {
      const section = cfg.sections.find((s) => s.id === sectionId);
      if (section) section.fields.splice(index, 1);
    });

  const addField = (sectionId) =>
    touch((cfg) => {
      const section = cfg.sections.find((s) => s.id === sectionId);
      if (!section) return;
      section.fields.push({
        id: makeFieldId("new_field"),
        label: "New Field",
        type: "text",
        required: false,
      });
    });

  const toggleOptions = (sectionId, fieldId) => {
    const key = `${sectionId}:${fieldId}`;
    setExpandedOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await saveSpecSheetFormConfig(config);
      setDirty(false);
      setStatus({ type: "success", message: "Form saved — changes are live for everyone." });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        "Reset the Blueprint Spec Sheet form to its built-in defaults? This removes all admin customizations."
      )
    )
      return;
    setSaving(true);
    setStatus(null);
    try {
      await resetSpecSheetFormConfig();
      setConfig(clone(DEFAULT_SPEC_SHEET_CONFIG));
      setDirty(false);
      setStatus({ type: "success", message: "Form reset to defaults." });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const renderFieldRow = (section, field, index) => {
    const optionsKey = `${section.id}:${field.id}`;
    const optionsOpen = !!expandedOptions[optionsKey];
    const hasOptions = field.type === "select" || field.type === "multiselect";

    return (
      <div
        key={field.id}
        className="app-surface rounded-xl border p-3"
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col">
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={() => moveField(section.id, index, -1)}
              aria-label={`Move ${field.label || "field"} up`}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={index === section.fields.length - 1}
              onClick={() => moveField(section.id, index, 1)}
              aria-label={`Move ${field.label || "field"} down`}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </div>

          <TextField
            label="Field Label"
            size="small"
            className="min-w-[180px] flex-1"
            value={field.label}
            onChange={(e) =>
              updateField(section.id, index, { label: e.target.value })
            }
          />

          <Select
            size="small"
            value={field.type}
            inputProps={{ "aria-label": "Field type" }}
            disabled={!!field.locked}
            onChange={(e) =>
              updateField(section.id, index, { type: e.target.value })
            }
          >
            {FIELD_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>

          <FormControlLabel
            control={
              <Checkbox
                checked={!!field.required}
                onChange={(e) =>
                  updateField(section.id, index, {
                    required: e.target.checked,
                  })
                }
              />
            }
            label="Required"
          />

          {hasOptions && (
            <Button
              size="small"
              endIcon={optionsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => toggleOptions(section.id, field.id)}
            >
              Options ({(field.options || []).length})
            </Button>
          )}

          <div className="ml-auto flex items-center gap-1">
            {field.locked ? (
              <Tooltip title="Built-in field — it can be renamed but not removed.">
                <LockIcon fontSize="small" className="app-muted" />
              </Tooltip>
            ) : (
              <IconButton
                size="small"
                color="error"
                onClick={() => removeField(section.id, index)}
                aria-label={`Remove ${field.label || "field"}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        </div>

        {hasOptions && optionsOpen && (
          <div className="mt-3">
            <TextField
              label="Dropdown values (one per line)"
              fullWidth
              multiline
              minRows={4}
              value={(field.options || []).join("\n")}
              onChange={(e) =>
                updateField(section.id, index, {
                  options: e.target.value.split("\n"),
                })
              }
              onBlur={(e) =>
                updateField(section.id, index, {
                  options: e.target.value
                    .split("\n")
                    .map((o) => o.trim())
                    .filter(Boolean),
                })
              }
            />
          </div>
        )}
      </div>
    );
  };

  const areas = Object.keys(AREA_LABELS);

  return (
    <ThemeProvider theme={muiTheme}>
    <div className="w-full max-w-5xl space-y-6">
      <SectionHeader
        className="!mb-0"
        title="Form Admin"
        description="Edit the Blueprint Spec Sheet: add or remove fields, mark them required, reorder them, and manage dropdown values. Changes apply to everyone once saved."
        actions={
          <>
            <Button color="warning" onClick={handleReset} disabled={saving}>
              Reset to Defaults
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !dirty}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </>
        }
      />

      {status && (
        <Notice
          tone={status.type === "success" ? "emerald" : "rose"}
          className="font-medium"
        >
          {status.message}
        </Notice>
      )}

      {dirty && !status && (
        <Notice tone="amber" className="font-medium">
          Unsaved changes — hit Save Changes to publish them.
        </Notice>
      )}

      {areas.map((area) => {
        const areaSections = config.sections.filter((s) => s.area === area);
        if (areaSections.length === 0) return null;
        return (
          <div key={area} className="space-y-4">
            <h2 className="pt-2 text-lg font-semibold tracking-tight">
              {AREA_LABELS[area]}
            </h2>
            {areaSections.map((section) => (
              <div
                key={section.id}
                className="app-card rounded-2xl border p-5"
              >
                <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <TextField
                    label="Section Title"
                    size="small"
                    value={section.title || ""}
                    onChange={(e) =>
                      updateSection(section.id, { title: e.target.value })
                    }
                  />
                  <TextField
                    label="Section Description"
                    size="small"
                    value={section.description || ""}
                    onChange={(e) =>
                      updateSection(section.id, {
                        description: e.target.value,
                      })
                    }
                  />
                </div>

                {section.special === "products" ? (
                  <p className="app-muted text-sm">
                    Built-in section — the product list and its modals are
                    managed by the sections below.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {section.fields.map((field, index) =>
                      renderFieldRow(section, field, index)
                    )}
                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() => addField(section.id)}
                    >
                      Add Field
                    </Button>
                  </div>
                )}

                {area === "floors" && (
                  <p className="app-muted mt-3 text-xs">
                    The floor level/material table (materials, depths, spacing,
                    deflection) is built-in and not editable here.
                  </p>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
    </ThemeProvider>
  );
};

export default SpecSheetFormAdmin;
